import React, { ChangeEvent } from 'react';
import {
  IBankTransaction,
  ITransferTransaction,
  TAttachment,
  TransactionTypeEnum
} from '@/services/types/TransactionData';
import { TTransactionHistory } from '../../types/TransactionHistory';
import { TDeleteAttachmentData } from '../../types/DeleteAttachmentData';
import { getStatementDetails } from '@/utils/getStatementDetails';
import AttachmentsSection from './AttachmentsSection';
import ToggleButton from '@/components/ToggleButton';
import ReceiptExcemptionReason from './ReceiptExemptionSection';
import { receiptReasonSelectInputOptions } from '../../data/receiptReasonSelectInputOptions';
import { TSelectOptions } from '@/components/Select';
import NoteInput from '../NoteInput';

type TRenderFooterData = {
  note: string;
  onChangeNote: (e: ChangeEvent<HTMLInputElement>) => void;
  isNoteSaved: boolean;
  transaction: TTransactionHistory;
  onDeleteAttachment: (data: TDeleteAttachmentData) => void;
  showAttachment?: boolean; // for purchase and emerge pay only
  onShowAttachmentChanged?: () => void; // for purchase and emerge pay only
  receiptExemptionReason?: TSelectOptions; // for purchase only
  onReceiptExemptionReasonSelected?: (option?: TSelectOptions, optionIndex?: number) => void;
  onFinishAttachmentUpload: (attachment: TAttachment) => void;
};

export const renderDetailFooter = ({
  note,
  onChangeNote,
  isNoteSaved,
  transaction,
  showAttachment,
  onDeleteAttachment,
  onShowAttachmentChanged: handleShowAttachmentChanged,
  receiptExemptionReason,
  onReceiptExemptionReasonSelected: handleReceiptExemptionReasonOptionSelected,
  onFinishAttachmentUpload
}: TRenderFooterData) => {
  switch (transaction.type) {
    case TransactionTypeEnum.Admin:
      return '';
    case TransactionTypeEnum.Transfer: {
      const transferTransaction = transaction.data as ITransferTransaction;
      return (
        <div>
          <span className="text-emerge-navy-2 text-sm">Description</span>
          <div className="bg-emerge-base-3 rounded-lg p-3 px-4 text-sm text-dark-navy-30 font-normal mt-3">
            {transferTransaction.description ? transferTransaction.description : 'No description'}
          </div>
        </div>
      );
    }
    case TransactionTypeEnum.Bank: {
      const bankTransaction = transaction.data as IBankTransaction;
      return (
        <div>
          <span className="text-emerge-navy-2 text-sm">Statement details</span>
          <div className="bg-emerge-base-3 rounded-lg p-3 px-4 text-sm text-dark-navy-30 font-normal mt-3">
            {getStatementDetails(bankTransaction)}
          </div>
        </div>
      );
    }
    case TransactionTypeEnum.TopUp: {
      return (
        <div>
          <NoteSection note={note} onChangeNote={onChangeNote} isNoteSaved={isNoteSaved} />
        </div>
      );
    }
    case TransactionTypeEnum.EmergePay: {
      return (
        <>
          <NoteSection note={note} onChangeNote={onChangeNote} isNoteSaved={isNoteSaved} />
          <div>
            <span className="text-emerge-navy-2 text-sm">Attachments</span>
            <AttachmentsSection transaction={transaction} onDeleteAttachment={onDeleteAttachment} />
          </div>
        </>
      );
    }
    case TransactionTypeEnum.Purchase: {
      return (
        <>
          <NoteSection note={note} onChangeNote={onChangeNote} isNoteSaved={isNoteSaved} />
          <div>
            <div className="flex justify-between">
              <span className="text-emerge-navy-2 text-sm">Attachments</span>
              <span>
                <ToggleButton
                  size="lg"
                  toggleState={showAttachment ?? false}
                  onChange={handleShowAttachmentChanged}>
                  {/* show 'yes' or 'no' based on current state */}
                  {showAttachment ? (
                    <span className="absolute left-2 text-xs bottom-0.5 text-emerge-navy">Yes</span>
                  ) : (
                    <span className="absolute right-2 text-xs bottom-0.5 text-white">No</span>
                  )}
                </ToggleButton>
              </span>
            </div>
            <>
              {showAttachment ? (
                <AttachmentsSection
                  transaction={transaction}
                  onDeleteAttachment={onDeleteAttachment}
                  onFinishUpload={onFinishAttachmentUpload}
                />
              ) : (
                <ReceiptExcemptionReason
                  value={receiptExemptionReason}
                  options={receiptReasonSelectInputOptions}
                  onOptionSelected={handleReceiptExemptionReasonOptionSelected!}
                />
              )}
            </>
          </div>
        </>
      );
    }
  }
};

const NoteSection: React.FC<{
  note: string;
  onChangeNote: (e: ChangeEvent<HTMLInputElement>) => void;
  isNoteSaved: boolean;
}> = ({ note, onChangeNote, isNoteSaved }) => (
  <div className="pb-4">
    <div className="text-emerge-navy-2 text-sm pb-2">Note</div>
    <NoteInput value={note} onTextChange={onChangeNote} isSaved={isNoteSaved} />
  </div>
);
